import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createHlsAdapter } from "../adapters/hls";
import { createWebrtcAdapter } from "../adapters/webrtc";
import { createRTSPWebRTCVideoAdapter } from "../adapters/rtsp-webrtc-video";
import { usePanZoom } from "../hooks/usePanZoom";
import { SingleView } from "./layouts/SingleView";
import { QuadView } from "./layouts/QuadView";
import { FlatView } from "./layouts/FlatView";
import { Timeline } from "./Timeline";
import { Controls } from "./Controls";
import { PTZControls } from "./PTZControls";
import { MiniMap } from "./MiniMap";
import { PiPOverlay } from "./PiPOverlay";
import "./styles.css";
import { CameraInfoOverlay } from "./CameraInfoOverlay";
import logger from "../../../utils/logger";

function makeAdapter(props) {
  if (props.source.live?.type === "webrtc")
    return createWebrtcAdapter(props.source.live, props.onStreamError);
  if (props.source.live?.type === "rtsp-webrtc")
    return createRTSPWebRTCVideoAdapter(props.source.live);
  if (props.source.live?.type === "hls" || props.source.vod?.type === "hls")
    return createHlsAdapter(props.source.live ?? props.source.vod);
  if (props.source.vod?.type === "mp4") {
    let video = null;
    return {
      name: "mp4",
      async attach(v) {
        video = v;
        // Set crossOrigin to allow canvas operations (snapshots) with CORS
        video.crossOrigin = "anonymous";
        video.src = props.source.vod.url;
        await video.play().catch(() => undefined);
      },
      async detach() {
        if (!video) return;
        video.removeAttribute("src");
        video.load();
        video = null;
      },
      async play() {
        await video?.play();
      },
      async pause() {
        video?.pause();
      },
    };
  }
  return null;
}

export const Camera360 = forwardRef(function Camera360(props, ref) {
  const videoRef = useRef(null);
  const handleRef = useRef(null);

  const adapter = useMemo(() => makeAdapter(props), [props]);
  const defaultMode = props.initialView?.mode ?? "bubble";

  // Determine initial video format based on camera type
  const getInitialVideoFormat = () => {
    if (props.cameraType === "360-degree" || props.cameraType === "85-degree") {
      return "360"; // 360-degree view for 360-degree and 85-degree cameras
    }
    return "flat"; // Flat view for all other camera types
  };

  const { view, setView, bind } = usePanZoom({
    yaw: props.initialView?.yaw ?? 0,
    pitch: props.initialView?.pitch ?? 0,
    fov: props.initialView?.fov ?? 110,
  });

  const [mode, setMode] = useState(defaultMode);
  const [playing, setPlaying] = useState(true);
  const [error, setError] = useState(null);
  const [speed, setSpeed] = useState(1);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [caps, setCaps] = useState({ ptz: false });
  const [pipPreset, setPipPreset] = useState(null);
  const [quadZoom, setQuadZoom] = useState(90);
  const [videoFormat, setVideoFormat] = useState(getInitialVideoFormat()); // Set based on camera type

  const presets = props.viewPresets ?? [
    { id: "front", label: "Front", yaw: 0, pitch: 0 },
    { id: "left", label: "Left", yaw: -90, pitch: 0 },
    { id: "right", label: "Right", yaw: 90, pitch: 0 },
    { id: "back", label: "Back", yaw: 180, pitch: 0 },
  ];

  useEffect(() => {
    (async () => {
      if (props.controlApi?.capabilities) {
        try {
          setCaps(await props.controlApi.capabilities());
        } catch {}
      }
    })();
  }, [props.controlApi?.capabilities]);

  useEffect(() => {
    if (!adapter || !videoRef.current) return;
    let disposed = false;
    const videoEl = videoRef.current;

    adapter
      .attach(videoEl)
      .then(() => {
        if (disposed) return;

        // ✅ Video event listeners
        const handlePlaying = () => {
          props.setLoader(false);
          setPlaying(true);
          props.onEvent?.({ type: "play" });
        };

        const handleCanPlay = () => {};

        const handleError = (err) => {
          console.error("❌ Video error:", err);
          props.setLoader(false);
          setError("Video playback error");
          props.onError?.({
            code: "VIDEO_ERROR",
            message: String(err?.message || err),
          });
        };

        // Attach listeners
        videoEl.addEventListener("playing", handlePlaying);
        videoEl.addEventListener("canplay", handleCanPlay);
        videoEl.addEventListener("error", handleError);

        // Cleanup on detach
        return () => {
          videoEl.removeEventListener("playing", handlePlaying);
          videoEl.removeEventListener("canplay", handleCanPlay);
          videoEl.removeEventListener("error", handleError);
        };
      })
      .catch((e) => {
        // Defensive guard: don't update state if component unmounted
        if (disposed) return;
        console.error("❌ Adapter attachment failed:", e);
        props.setLoader(false);
        setError(e?.message || "Failed to attach stream");
        props.onError?.({
          code: e?.code || "ATTACH_FAILED",
          message: String(e?.message || e),
        });
      });

    // Cleanup when effect re-runs or component unmounts
    return () => {
      disposed = true;
      // Ensure adapter is properly detached
      if (adapter && typeof adapter.detach === "function") {
        adapter.detach().catch((err) => {
          // Log but don't throw - cleanup should be best-effort
          logger.error("Error during adapter detach:", err);
        });
      }
    };
  }, [adapter]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Defensive guard: track if component is mounted
    let isMounted = true;

    const onLoaded = () => {
      if (!isMounted) return;
      setDuration(isFinite(v.duration) ? v.duration : 0);
    };
    const onTime = () => {
      if (!isMounted) return;
      setCurrent(v.currentTime || 0);
    };
    const onError = (e) => {
      console.error("Video error:", e, "error code:", v.error?.code);
    };
    const onCanPlay = () => {};
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("durationchange", onLoaded);
    v.addEventListener("timeupdate", onTime);
    v.addEventListener("error", onError);
    v.addEventListener("canplay", onCanPlay);
    return () => {
      isMounted = false;
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("durationchange", onLoaded);
      v.removeEventListener("timeupdate", onTime);
      v.removeEventListener("error", onError);
      v.removeEventListener("canplay", onCanPlay);
    };
  }, []);

  // Expose handle
  useImperativeHandle(ref, () => handleRef.current);

  // Fill handleRef
  handleRef.current = {
    setView: (v) => setView((cur) => ({ ...cur, ...v })),
    goToTime: (t) => {
      const v = videoRef.current;
      if (!v) return;
      const x = t instanceof Date ? t.getTime() / 1000 : t;
      v.currentTime = x;
      props.onEvent?.({ type: "seek", to: x });
    },
    takeSnapshot: async () => {
      const v = videoRef.current;
      if (!v) return;

      const c = document.createElement("canvas");
      c.width = v.videoWidth;
      c.height = v.videoHeight;

      const ctx = c.getContext("2d");
      if (!ctx) return;

      ctx.drawImage(v, 0, 0);

      c.toBlob((blob) => {
        if (!blob) return;
        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = `snapshot-${Date.now()}.png`;
        document.body.appendChild(a);
        a.style.display = "none";

        a.click();

        // Cleanup: remove element and revoke blob URL
        document.body.removeChild(a);
        // Use setTimeout to ensure download starts before revoking
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 100);
      }, "image/png");
    },
    play: () => videoRef.current?.play(),
    pause: () => videoRef.current?.pause(),
  };

  const toggleBrowserPiP = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (document.pictureInPictureElement) {
      document.exitPictureInPicture?.();
      return;
    }
    try {
      await v.requestPictureInPicture?.();
    } catch {}
  };

  const theme = props.ui?.theme ?? "auto";
  const showMini = props.ui?.miniMap?.enabled ?? true;
  const showBrowserPipBtn = props.ui?.pip?.enabled ?? true;
  const showPTZ = mode === "bubble" && videoFormat === "360";

  const onPinFromQuad = (p) => {
    if (!(props.ui?.pip?.miniWindow?.enabled ?? true)) return;
    setPipPreset(p);
  };

  // PTZ movement handler
  const handlePTZMove = (pan, tilt, zoom) => {
    const panSpeed = 10;
    const tiltSpeed = 10;
    const zoomSpeed = 5;

    setView((currentView) => ({
      ...currentView,
      yaw: currentView.yaw + pan * panSpeed,
      pitch: Math.max(-90, Math.min(90, currentView.pitch + tilt * tiltSpeed)),
      fov: Math.max(10, Math.min(120, currentView.fov - zoom * zoomSpeed)),
    }));
  };

  const handlePTZStop = () => {
    setView((currentView) => ({
      ...currentView,
      yaw: 0,
      pitch: 0,
      fov: 75,
    }));
  };

  const handlePTZPreset = (preset) => {
    setView((currentView) => ({
      ...currentView,
      yaw: preset.yaw,
      pitch: preset.pitch,
      fov: preset.fov ?? currentView.fov,
    }));
  };

  return (
    <div
      className={`cam360-root theme-${theme} ${props.className ?? ""}`}
      style={props.style}
      aria-label={props.accessibility?.ariaLabel}
    >
      {/* {error && <div className="cam360-error">{error}</div>} */}
      <video
        ref={videoRef}
        className="cam360-video-hidden"
        playsInline
        muted
        controls={false}
      />

      {videoRef.current &&
        (videoFormat === "flat" ? (
          <FlatView video={videoRef.current} overlays={props.overlays} />
        ) : mode === "quad" ? (
          <QuadView
            video={videoRef.current}
            baseView={view}
            presets={presets}
            onPin={onPinFromQuad}
            perf={props.performance}
            quadZoom={quadZoom}
            onQuadZoomChange={setQuadZoom}
          />
        ) : (
          <SingleView
            video={videoRef.current}
            view={view}
            overlays={props.overlays}
            bind={bind}
            perf={props.performance}
          />
        ))}

      {pipPreset && videoRef.current && (
        <PiPOverlay
          video={videoRef.current}
          yaw={pipPreset.yaw}
          pitch={pipPreset.pitch}
          fov={pipPreset.fov ?? view.fov}
          onClose={() => setPipPreset(null)}
        />
      )}

      {/* {showMini && videoRef.current && (
        <MiniMap
          video={videoRef.current}
          yaw={view.yaw}
          pitch={view.pitch}
          fov={view.fov}
          position={props.ui?.miniMap?.position ?? "tr"}
          size={props.ui?.miniMap?.size ?? 180}
          onJump={(yaw, pitch) => setView((v) => ({ ...v, yaw, pitch }))}
        />
      )} */}

      <Controls
        playing={playing && !videoRef.current?.paused}
        onTogglePlay={() => {
          const v = videoRef.current;
          if (!v) return;
          v.paused ? v.play() : v.pause();
          setPlaying(!playing);
        }}
        onSnapshot={() => handleRef.current?.takeSnapshot()}
        onFullscreen={() => {
          const quad = document.querySelector(".cam360-quad");
          const single = document.querySelector(".cam360-stage");
          const el = mode === "quad" ? quad : single;
          if (!el) return;
          if (!document.fullscreenElement) el.requestFullscreen?.();
          else document.exitFullscreen?.();
        }}
        onTogglePip={showBrowserPipBtn ? toggleBrowserPiP : undefined}
        speed={speed}
        onSpeed={(s) => {
          setSpeed(s);
          const v = videoRef.current;
          if (v) v.playbackRate = s;
        }}
        presets={videoFormat === "360" ? presets : undefined}
        onPreset={(p) =>
          setView((v) => ({
            ...v,
            yaw: p.yaw,
            pitch: p.pitch,
            fov: p.fov ?? v.fov,
          }))
        }
        mode={mode}
        onModeChange={videoFormat === "360" ? setMode : undefined}
        videoFormat={videoFormat}
        onVideoFormatChange={setVideoFormat}
        isHovered={props.isHovered}
        selectDropDown={props.selectDropDown}
        quadView={props.quadView}
      />

      {mode === "quad" && videoFormat === "360" && (
        <div
          className={`cam360-quad-zoom-overlay ${
            props.isHovered ? "visible" : "hidden"
          }`}
          style={{
            opacity: props.isHovered ? 1 : 0,
            transform: props.isHovered ? "translateY(0)" : "translateY(-10px)",
            transition: "all 0.3s ease",
          }}
        >
          <div className="zoom-controls-compact">
            <div className="zoom-slider-container">
              <span className="zoom-label">Zoom</span>
              <input
                type="range"
                min="30"
                max="110"
                step="1"
                value={quadZoom}
                onChange={(e) => setQuadZoom(Number(e.target.value))}
                className="zoom-slider"
              />
              <span className="zoom-value">
                {Math.round(((110 - quadZoom) / (110 - 30)) * 100)}%
              </span>
            </div>
            <button
              className="reset-zoom-btn"
              onClick={() => setQuadZoom(90)}
              title="Reset Zoom"
            >
              🔄
            </button>
          </div>
        </div>
      )}

      {(props.ui?.controls?.timeline ?? true) && duration > 0 && (
        <Timeline
          current={current}
          duration={duration}
          onSeek={(t) => {
            const v = videoRef.current;
            if (!v) return;
            v.currentTime = t;
            props.onEvent?.({ type: "seek", to: t });
          }}
        />
      )}
      <CameraInfoOverlay
        pathName={props.cameraName}
        mediaMtxDns={props.mediaMtxDns}
      />

      {/* 
      {showPTZ && (
        <PTZControls 
          onMove={handlePTZMove}
          onStop={handlePTZStop}
          onGotoPreset={handlePTZPreset}
          presets={presets}
        />
      )} */}

      {/* <div className="cam360-mode-toggle">
        <button
          onClick={() => setMode("bubble")}
          className={mode === "bubble" ? "active" : ""}
        >
          Single
        </button>
        <button
          onClick={() => setMode("quad")}
          className={mode === "quad" ? "active" : ""}
        >
          Quad
        </button>
      </div> */}
    </div>
  );
});
